import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useParams } from 'react-router-dom';

import { UserContext } from 'app/modules/Context';
import { routes } from 'app/routes';
import { fileUploaderService } from 'pkg/components/FileUploader/fileUploaderService';

import { useRedirectToUpdater } from '../../UpdaterEmailForm/useRedirectToUpdater';

export function useFileUploader() {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user } = useContext(UserContext);
  const email = user?.email || '';

  const [shouldShowTrigger, setShouldShowTrigger] = useState(false);
  const ref = useRef<{ openModal: () => void }>(null);
  const timeoutRef = useRef(0);
  const { isLoading, data } = useQuery(['files', id], fileUploaderService.getFilesByQuote);
  const redirect = useRedirectToUpdater(routes.TRANSLATION_QUOTE.FORM);

  const { mutate, isLoading: isMutating } = useMutation(fileUploaderService.updateUploadedFiles(id), {
    onSuccess: () => {
      redirect({ id, email });
    },
  });

  useLayoutEffect(() => {
    const handleMouseOut = (event: MouseEvent) => {
      // The location check is made to prevent displaying 2 dialogs at the same time (Need it sooner and File Uploader).
      if (
        event.relatedTarget !== null ||
        shouldShowTrigger ||
        location.pathname.includes(routes.NEED_IT_SOONER) ||
        !!data?.length
      ) {
        return;
      }
      ref.current?.openModal();
      setShouldShowTrigger(true);
    };

    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      document.removeEventListener('mouseout', handleMouseOut);
      window.clearTimeout(timeoutRef.current);
    };
  }, [shouldShowTrigger, location.pathname, data]);

  const onLastSlideDisplayed = () => {
    if (shouldShowTrigger) {
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      ref.current?.openModal();
      setShouldShowTrigger(true);
    }, 5000);
  };

  const onFilesUploaded = (newFiles: string[]) => {
    if (newFiles.length === data?.length && newFiles.every((name) => data?.some((file) => file === name))) {
      return;
    }
    mutate(newFiles);
  };

  const onClickFilesUploaded = () => {
    ref.current?.openModal();
    setShouldShowTrigger(true);
  };

  return {
    onLastSlideDisplayed,
    isLoading: isLoading || isMutating,
    ref,
    onFilesUploaded,
    files: data,
    shouldShowTrigger,
    onClickFilesUploaded,
  };
}
